import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Back-office toasts (Admin-* « Pour les devs »): bottom centre, #1B1C31, white 12.5 px, radius 14, min height 43,
 * max width 620, icon on the left; 1.7 s, or 6 s for errors and warnings. One at a time.
 */
type ToastTone = "default" | "success" | "error";
interface Toast {
  id: number;
  message: ReactNode;
  detail?: ReactNode;
  tone: ToastTone;
}

const ToastCtx = createContext<(message: ReactNode, tone?: ToastTone, detail?: ReactNode) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const push = useCallback((message: ReactNode, tone: ToastTone = "default", detail?: ReactNode) => {
    const id = Date.now() + Math.random();
    setToast({ id, message, detail, tone });
    setTimeout(() => setToast((t) => (t?.id === id ? null : t)), tone === "error" ? 6000 : 2400);
  }, []);
  return (
    <ToastCtx value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-4 bottom-6 z-[70] flex justify-center">
        {toast && (
          <div
            key={toast.id}
            role={toast.tone === "error" ? "alert" : "status"}
            className="pointer-events-auto flex min-h-[43px] max-w-[620px] items-center gap-2.5 rounded-[14px] bg-ink px-4 py-2.5 text-[12.5px] font-medium leading-snug text-white shadow-flyout animate-[pop_.18s_ease-out]"
          >
            {toast.tone === "success" && <CheckCircle2 className="size-4 shrink-0 text-lime" />}
            {toast.tone === "error" && <AlertCircle className="size-4 shrink-0 text-[#FF8A80]" />}
            {toast.tone === "default" && <Info className="size-4 shrink-0 text-[#C9CEF7]" />}
            <span className={cn("min-w-0 break-words")}>
              <span className="font-bold">{toast.message}</span>
              {toast.detail && <span className="block text-white/70">{toast.detail}</span>}
            </span>
          </div>
        )}
      </div>
    </ToastCtx>
  );
}

export const useToast = () => useContext(ToastCtx);
